/**
 * The dashboard.
 *
 * ═══════════════════════════════════════════════════════════════════
 * EVERY FIGURE HERE IS COMPUTED FROM POSTED TRANSACTIONS.
 *
 * Nothing on this page is illustrative. The creditors figure is the sum of
 * outstanding purchase invoices; the commitments figure is the sum of open
 * commitment records; the surplus is income less expenditure from the balance
 * table. A card with nothing behind it says so rather than showing a plausible
 * number, because a dashboard that invents figures teaches people to distrust
 * the ones that are real.
 * ═══════════════════════════════════════════════════════════════════
 *
 * Which cards appear is decided twice over: the server returns only the cards
 * the caller's permissions entitle them to, and the tenant configuration states
 * the order they appear in. A department user and a finance director signing in
 * to the same URL get genuinely different pages.
 */

import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import { useSession } from '../lib/session';
import { useTenant, lower } from '../lib/tenant';
import { date, dateTime, money, percent, relativeTime, statusLabel, statusTone, utilisationTextClass } from '../lib/format';
import { AsyncPanel, Badge, Card, DataTable, EmptyState, Money, PageHeader, Progress, Stat } from '../components/ui';

interface PendingApproval {
  requestId: string;
  subjectType: string;
  subjectId: string;
  subjectReference: string;
  subjectSummary: string;
  valueMinor: number;
  stageName: string;
  originatorName: string;
  costCentreCode: string | null;
  dueAt: string | null;
  isOverdue: boolean;
  onBehalfOfName: string | null;
}

interface DashboardResponse {
  entityId: string;
  financialYear: { id: string; code: string; name: string };
  currentPeriod: { id: string; name: string; period_no: number } | null;
  generatedAt: string;
  availableCards: string[];
  cards: {
    myApprovals?: { count: number; overdueCount: number; totalValueMinor: number; items: PendingApproval[] };
    myRequisitions?: { byStatus: Array<{ status: string; count: number; totalMinor: number }> };
    myBudgets?: {
      budgetVersion: { id: string; code: string; name: string } | null;
      costCentres: Array<{
        costCentreId: string;
        costCentreCode: string;
        costCentreName: string;
        budgetMinor: number;
        actualMinor: number;
        commitmentMinor: number;
        availableMinor: number;
        utilisationPercent: number;
      }>;
    };
    cash?: { accounts: Array<{ code: string; name: string; balanceMinor: number; unreconciledCount: number }>; totalMinor: number };
    creditors?: {
      outstandingMinor: number;
      invoiceCount: number;
      overdueMinor: number;
      overdueCount: number;
      inProgress: Array<{ status: string; count: number; totalMinor: number }>;
    };
    debtors?: { outstandingMinor: number; invoiceCount: number; overdueMinor: number; overdueCount: number };
    incomeExpenditure?: {
      periodName: string | null;
      incomeYtdMinor: number;
      expenditureYtdMinor: number;
      surplusYtdMinor: number;
    };
    commitments?: { hardMinor: number; softMinor: number; count: number };
    anomalies?: {
      total: number;
      bySeverity: Array<{ severity: string; count: number }>;
      recent: Array<{ id: string; title: string; severity: string; description: string; detected_at: string }>;
    };
    integrations?: { byStatus: Array<{ status: string; count: number }>; openErrorCount: number };
    monthEnd?: {
      periodName: string | null;
      tasks: Array<{ status: string; severity: string; count: number }>;
      outstandingBlockers: Array<{ status: string; severity: string; count: number }>;
    };
    recentActivity?: Array<{ id: string; event_type: string; summary: string; actor_name: string | null; occurred_at: string }>;
  };
}

interface ContractReminder {
  id: string;
  reminder_type: string;
  due_date: string;
  title: string;
  contract_id: string;
  reference: string;
  contract_title: string;
  entity_code: string;
  annual_value_minor: number;
}

/*
 * Where an item waiting for approval can actually be opened.
 *
 * Only subject types with a DETAIL route belong here. The map used to include
 * sales invoice requests, supplier requests, budget transfers and expense
 * claims, whose list screens exist but which have no `/:id` route — so the link
 * resolved to the catch-all, which redirects to "/", which is the dashboard the
 * reader was already looking at. Clicking a reference appeared to do nothing at
 * all, which is the worst kind of broken control: it looks like the application
 * ignored you.
 *
 * Anything not listed here is rendered as plain text rather than as a link that
 * goes nowhere.
 */
const SUBJECT_PATHS: Record<string, string> = {
  purchase_requisition: '/requisitions',
  purchase_invoice: '/invoices',
  payment_batch: '/payments',
};

/** How many cost centres the dashboard summarises before deferring to Budgets. */
const DASHBOARD_COST_CENTRES = 8;

/**
 * Cards that span the whole grid row because they carry a wide financial table.
 * They are placed last so that no card before them is left stranded beside an
 * empty half-row.
 */
const WIDE_CARDS = new Set(['myBudgets']);

/** "Good morning" before noon, and so on — UK local time, like every other clock here. */
function greeting(): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', hour: '2-digit', hour12: false }).format(new Date()),
  );
  if (hour < 12) return 'Good morning';
  if (hour < 18) return 'Good afternoon';
  return 'Good evening';
}

export function DashboardPage() {
  const { user, can } = useSession();
  const { terminology: t, configuration, branding, moduleEnabled } = useTenant();

  const dash = useAsync(() => api.get<DashboardResponse>('/dashboard'), [user?.entityId]);
  const reminders = useAsync(
    () =>
      moduleEnabled('contractIntelligence') && can('contract.view')
        ? api.get<{ reminders: ContractReminder[] }>('/platform/contracts/reminders/due?withinDays=180')
        : Promise.resolve({ reminders: [] as ContractReminder[] }),
    [user?.entityId, configuration?.profileKey],
  );

  const entityName = user?.entities.find((e) => e.entityId === user.entityId)?.entityName ?? '';

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user?.fullName.split(' ')[0] ?? ''}`}
        subtitle={
          <>
            {user?.jobTitle} · {entityName}
          </>
        }
        meta={
          /*
           * The year and period are also in the top bar — but the top bar drops
           * them on a narrow screen, where this becomes the only statement of
           * which period the figures below belong to. The year arrives from the
           * API as `code`; this asked for `name`, which the response does not
           * carry, so the label "Financial year" previously stood above nothing
           * at all.
           */
          dash.data ? (
            <>
              <span>
                Financial year <strong>{dash.data.financialYear.code}</strong>
              </span>
              {dash.data.currentPeriod ? (
                <span>
                  Period <strong>{dash.data.currentPeriod.name}</strong>
                </span>
              ) : null}
              <span className="muted smaller">Figures as at {dateTime(dash.data.generatedAt)}</span>
            </>
          ) : null
        }
      />

      <AsyncPanel state={dash}>
        {(data) => {
          const order = configuration?.dashboard?.cards ?? Object.keys(data.cards);
          const present = new Set(data.availableCards);
          const all = [...order.filter((c) => present.has(c)), ...data.availableCards.filter((c) => !order.includes(c))];
          /*
           * Full-width cards are rendered after the two-column ones.
           *
           * A card that spans both columns starts a new grid row, so any card
           * placed before it leaves the other half of its row empty — a visible
           * hole in the middle of the first screen an evaluator sees. Ordering
           * them out rather than relying on dense auto-flow keeps the reading
           * order a screen reader follows identical to the order on screen.
           */
          const ordered = [...all.filter((c) => !WIDE_CARDS.has(c)), ...all.filter((c) => WIDE_CARDS.has(c))];

          return (
            <div className="stack">
              {/* The headline row: what the money is doing. */}
              <div className="grid grid--4">
                {data.cards.incomeExpenditure ? (
                  /*
                   * A budget holder's figures are scoped to her cost centres,
                   * and a department has costs but no income. Income less
                   * expenditure is then just expenditure with a minus sign in
                   * front of it, and calling that a "surplus" put a large red
                   * deficit on the first screen a budget holder sees — for a
                   * department that is running entirely to plan. When there is
                   * no income in scope, the card says what the figure actually
                   * is.
                   */
                  data.cards.incomeExpenditure.incomeYtdMinor === 0 ? (
                    <Stat
                      label="Expenditure year to date"
                      value={money(data.cards.incomeExpenditure.expenditureYtdMinor)}
                      detail={
                        data.cards.incomeExpenditure.periodName
                          ? `To the end of ${data.cards.incomeExpenditure.periodName}`
                          : 'Posted transactions only'
                      }
                      to={can('report.run') ? '/reports/INCOME_EXPENDITURE' : undefined}
                      drillLabel="Income and expenditure"
                    />
                  ) : (
                    <Stat
                      label="Surplus year to date"
                      value={money(data.cards.incomeExpenditure.surplusYtdMinor)}
                      tone={data.cards.incomeExpenditure.surplusYtdMinor >= 0 ? 'positive' : 'negative'}
                      detail={`Income ${money(data.cards.incomeExpenditure.incomeYtdMinor)} · Expenditure ${money(
                        data.cards.incomeExpenditure.expenditureYtdMinor,
                      )}`}
                      to={can('report.run') ? '/reports/INCOME_EXPENDITURE' : undefined}
                      drillLabel="Income and expenditure"
                    />
                  )
                ) : null}
                {data.cards.cash ? (
                  <Stat
                    label="Cash at bank"
                    value={money(data.cards.cash.totalMinor)}
                    detail={`${data.cards.cash.accounts.length} account${data.cards.cash.accounts.length === 1 ? '' : 's'}`}
                    to={can('cash.view') ? '/cash' : undefined}
                    drillLabel="Cash book"
                  />
                ) : null}
                {data.cards.creditors ? (
                  <Stat
                    label="Owed to suppliers"
                    value={money(data.cards.creditors.outstandingMinor)}
                    detail={
                      data.cards.creditors.overdueCount > 0 ? (
                        <span className="num--negative">
                          {money(data.cards.creditors.overdueMinor)} overdue on {data.cards.creditors.overdueCount}{' '}
                          invoice{data.cards.creditors.overdueCount === 1 ? '' : 's'}
                        </span>
                      ) : (
                        `${data.cards.creditors.invoiceCount} invoice${data.cards.creditors.invoiceCount === 1 ? '' : 's'} outstanding, none overdue`
                      )
                    }
                    to={can('ap.view') ? '/invoices' : undefined}
                    drillLabel="Supplier invoices"
                  />
                ) : null}
                {data.cards.commitments ? (
                  <Stat
                    label="Committed, not yet spent"
                    value={money(data.cards.commitments.hardMinor + data.cards.commitments.softMinor)}
                    detail={`${money(data.cards.commitments.hardMinor)} on order · ${money(
                      data.cards.commitments.softMinor,
                    )} requested`}
                    to={can('report.run') ? '/reports/COMMITMENTS' : undefined}
                    drillLabel="Commitment report"
                  />
                ) : null}
              </div>

              <div className="grid grid--2">
                {ordered.map((key) => {
                  switch (key) {
                    case 'myApprovals':
                      return data.cards.myApprovals ? (
                        <Card
                          key={key}
                          title="Waiting for you"
                          subtitle={
                            data.cards.myApprovals.count === 0
                              ? 'Nothing needs your approval.'
                              : `${data.cards.myApprovals.count} item${data.cards.myApprovals.count === 1 ? '' : 's'}, ${money(
                                  data.cards.myApprovals.totalValueMinor,
                                )}${data.cards.myApprovals.overdueCount > 0 ? ` — ${data.cards.myApprovals.overdueCount} overdue` : ''}`
                          }
                          actions={
                            data.cards.myApprovals.count > 0 ? (
                              <Link className="btn btn--sm" to="/approvals">
                                Review all
                              </Link>
                            ) : null
                          }
                          flush
                        >
                          {data.cards.myApprovals.items.length === 0 ? (
                            <div style={{ padding: '1rem' }}>
                              <EmptyState title="Your approval queue is clear" icon="✓" />
                            </div>
                          ) : (
                            <DataTable
                              caption="Items awaiting your approval"
                              rows={data.cards.myApprovals.items}
                              rowKey={(r) => r.requestId}
                              columns={[
                                {
                                  key: 'ref',
                                  header: 'Reference',
                                  render: (r) => {
                                    const path = SUBJECT_PATHS[r.subjectType];
                                    return path ? (
                                      <Link to={`${path}/${r.subjectId}`}>
                                        <span className="mono">{r.subjectReference}</span>
                                      </Link>
                                    ) : (
                                      <span className="mono">{r.subjectReference}</span>
                                    );
                                  },
                                },
                                {
                                  key: 'what',
                                  header: 'What',
                                  render: (r) => (
                                    <>
                                      <div className="truncate">{r.subjectSummary}</div>
                                      <div className="smaller muted">
                                        {r.originatorName}
                                        {r.costCentreCode ? ` · ${r.costCentreCode}` : ''}
                                        {r.onBehalfOfName ? ` · on behalf of ${r.onBehalfOfName}` : ''}
                                      </div>
                                    </>
                                  ),
                                },
                                { key: 'value', header: 'Value', numeric: true, render: (r) => <Money minor={r.valueMinor} /> },
                                {
                                  key: 'due',
                                  header: 'Due',
                                  render: (r) =>
                                    r.isOverdue ? (
                                      <Badge tone="error">Overdue</Badge>
                                    ) : (
                                      <span className="smaller muted">{r.dueAt ? relativeTime(r.dueAt) : '—'}</span>
                                    ),
                                },
                              ]}
                            />
                          )}
                        </Card>
                      ) : null;

                    case 'myBudgets':
                      return data.cards.myBudgets ? (
                        <Card
                          key={key}
                          title={`My ${lower(t.costCentrePlural)}`}
                          subtitle={
                            data.cards.myBudgets.budgetVersion
                              ? `Against ${data.cards.myBudgets.budgetVersion.name}`
                              : 'No approved budget version'
                          }
                          actions={
                            <Link className="btn btn--sm" to="/budgets">
                              Open budgets
                            </Link>
                          }
                          flush
                          wide
                        >
                          {data.cards.myBudgets.costCentres.length === 0 ? (
                            <div style={{ padding: '1rem' }}>
                              <EmptyState title={`No ${lower(t.costCentrePlural)} are assigned to you`} />
                            </div>
                          ) : (
                            /*
                             * A summary, not the report.
                             *
                             * A director holds fifteen or more cost centres and
                             * six money columns; all of it in half a card meant
                             * the last column was clipped at the card edge and a
                             * figure appeared to end mid-digit. The dashboard
                             * answers "which of mine need me?" — closest to
                             * their limit first — and the full position is one
                             * click away in Budgets.
                             */
                            <DataTable
                              caption={`Budget position for the ${lower(t.costCentrePlural)} you hold, closest to their limit first`}
                              rows={[...data.cards.myBudgets.costCentres]
                                .sort((a, b) => b.utilisationPercent - a.utilisationPercent)
                                .slice(0, DASHBOARD_COST_CENTRES)}
                              rowKey={(r) => r.costCentreId}
                              columns={[
                                {
                                  key: 'cc',
                                  header: t.costCentre,
                                  render: (r) => (
                                    <>
                                      <span className="mono smaller">{r.costCentreCode}</span>{' '}
                                      <span className="truncate">{r.costCentreName}</span>
                                    </>
                                  ),
                                },
                                { key: 'budget', header: 'Budget', numeric: true, width: '11rem', render: (r) => <Money minor={r.budgetMinor} /> },
                                { key: 'actual', header: 'Spent', numeric: true, width: '11rem', render: (r) => <Money minor={r.actualMinor} /> },
                                {
                                  key: 'committed',
                                  header: 'Committed',
                                  numeric: true,
                                  width: '11rem',
                                  render: (r) => <Money minor={r.commitmentMinor} blankIfZero />,
                                },
                                {
                                  key: 'available',
                                  header: 'Available',
                                  numeric: true,
                                  width: '11rem',
                                  render: (r) => <Money minor={r.availableMinor} bold />,
                                },
                                {
                                  key: 'used',
                                  header: 'Used',
                                  width: '8.5rem',
                                  render: (r) => (
                                    <>
                                      <Progress
                                        percent={r.utilisationPercent}
                                        label={`${r.costCentreName} budget used`}
                                      />
                                      <span className={`smaller ${utilisationTextClass(r.utilisationPercent)}`}>
                                        {percent(r.utilisationPercent)}
                                      </span>
                                    </>
                                  ),
                                },
                              ]}
                              footer={
                                data.cards.myBudgets.costCentres.length > DASHBOARD_COST_CENTRES ? (
                                  <tr>
                                    <td colSpan={6} className="smaller muted">
                                      Showing {DASHBOARD_COST_CENTRES} of {data.cards.myBudgets.costCentres.length}{' '}
                                      {lower(t.costCentrePlural)}. Open budgets for the full position, including spend
                                      and commitments.
                                    </td>
                                  </tr>
                                ) : null
                              }
                            />
                          )}
                        </Card>
                      ) : null;

                    case 'myRequisitions':
                      return data.cards.myRequisitions && data.cards.myRequisitions.byStatus.length > 0 ? (
                        <Card
                          key={key}
                          title={`My ${lower(t.requisitionPlural)}`}
                          actions={
                            can('requisition.create') ? (
                              <Link className="btn btn--sm btn--primary" to="/requisitions/new">
                                New {lower(t.requisition)}
                              </Link>
                            ) : null
                          }
                        >
                          <ul className="stack stack--sm">
                            {data.cards.myRequisitions.byStatus.map((s) => (
                              <li key={s.status} className="row row--between">
                                <span>
                                  <Badge tone={statusTone(s.status)}>{statusLabel(s.status)}</Badge>{' '}
                                  <span className="muted smaller">
                                    {s.count} {s.count === 1 ? lower(t.requisition) : lower(t.requisitionPlural)}
                                  </span>
                                </span>
                                <Money minor={s.totalMinor} />
                              </li>
                            ))}
                          </ul>
                        </Card>
                      ) : null;

                    case 'debtors':
                      return data.cards.debtors ? (
                        <Card
                          key={key}
                          title="Owed to us"
                          subtitle="Sales ledger, outstanding"
                          actions={
                            <Link className="btn btn--sm" to="/debtors">
                              Credit control
                            </Link>
                          }
                        >
                          <div className="grid grid--2">
                            <Stat
                              label="Outstanding"
                              value={money(data.cards.debtors.outstandingMinor)}
                              detail={`${data.cards.debtors.invoiceCount} invoice${data.cards.debtors.invoiceCount === 1 ? '' : 's'}`}
                            />
                            <Stat
                              label="Overdue"
                              value={money(data.cards.debtors.overdueMinor)}
                              tone={data.cards.debtors.overdueMinor > 0 ? 'negative' : undefined}
                              detail={`${data.cards.debtors.overdueCount} invoice${data.cards.debtors.overdueCount === 1 ? '' : 's'}`}
                            />
                          </div>
                        </Card>
                      ) : null;

                    case 'anomalies':
                      return data.cards.anomalies && data.cards.anomalies.total > 0 ? (
                        <Card
                          key={key}
                          title="Things worth a look"
                          subtitle="Rule-based checks over posted data. Each one is a prompt to a person, never an automatic action."
                        >
                          <ul className="timeline">
                            {data.cards.anomalies.recent.map((a) => (
                              <li
                                key={a.id}
                                className={
                                  a.severity === 'high' || a.severity === 'critical'
                                    ? 'timeline__item timeline__item--error'
                                    : 'timeline__item timeline__item--warn'
                                }
                              >
                                <p className="timeline__title">{a.title}</p>
                                <p className="timeline__detail">{a.description}</p>
                                <p className="timeline__time">{relativeTime(a.detected_at)}</p>
                              </li>
                            ))}
                          </ul>
                        </Card>
                      ) : null;

                    case 'monthEnd':
                      return data.cards.monthEnd ? (
                        <Card
                          key={key}
                          title="Period close"
                          subtitle={data.cards.monthEnd.periodName ?? undefined}
                          actions={
                            <Link className="btn btn--sm" to="/close">
                              Close centre
                            </Link>
                          }
                        >
                          {data.cards.monthEnd.tasks.length === 0 ? (
                            <EmptyState title="No close tasks recorded for this period" />
                          ) : (
                            <ul className="stack stack--sm">
                              {data.cards.monthEnd.tasks.map((task) => (
                                <li key={`${task.status}-${task.severity}`} className="row row--between">
                                  <span>
                                    <Badge tone={statusTone(task.status)}>{statusLabel(task.status)}</Badge>{' '}
                                    <span className="muted smaller">{statusLabel(task.severity)}</span>
                                  </span>
                                  <span className="num">{task.count}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </Card>
                      ) : null;

                    case 'integrations':
                      return data.cards.integrations ? (
                        <Card
                          key={key}
                          title="Integrations"
                          subtitle="All connectors in this build are demonstration adapters."
                          actions={
                            <Link className="btn btn--sm" to="/integrations">
                              Integration hub
                            </Link>
                          }
                        >
                          <div className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
                            {data.cards.integrations.byStatus.map((s) => (
                              <Badge key={s.status} tone={statusTone(s.status)}>
                                {s.count} {statusLabel(s.status)}
                              </Badge>
                            ))}
                            {data.cards.integrations.openErrorCount > 0 ? (
                              <Badge tone="error">{data.cards.integrations.openErrorCount} records in the error queue</Badge>
                            ) : null}
                          </div>
                          <p className="smaller muted" style={{ marginTop: '0.75rem' }}>
                            A record that fails validation is queued for a decision, never discarded.
                          </p>
                        </Card>
                      ) : null;

                    case 'recentActivity':
                      return data.cards.recentActivity && data.cards.recentActivity.length > 0 ? (
                        <Card
                          key={key}
                          title="Recent activity"
                          subtitle="From the append-only audit trail"
                          actions={
                            can('audit.view') ? (
                              <Link className="btn btn--sm" to="/audit">
                                Audit trail
                              </Link>
                            ) : null
                          }
                        >
                          <ul className="timeline">
                            {data.cards.recentActivity.slice(0, 8).map((a) => (
                              <li key={a.id} className="timeline__item">
                                <p className="timeline__title">{a.summary}</p>
                                <p className="timeline__time">
                                  {a.actor_name ? `${a.actor_name} · ` : ''}
                                  {relativeTime(a.occurred_at)}
                                </p>
                              </li>
                            ))}
                          </ul>
                        </Card>
                      ) : null;

                    default:
                      return null;
                  }
                })}

                {/* Contract renewals: only where the module is on and something is due. */}
                {reminders.data && reminders.data.reminders.length > 0 ? (
                  <Card
                    key="contractRenewals"
                    title="Contract deadlines"
                    subtitle="Notice deadlines come first: after that date an auto-renewing contract commits another term."
                    actions={
                      <Link className="btn btn--sm" to="/contracts">
                        Contract register
                      </Link>
                    }
                    flush
                  >
                    <DataTable
                      caption="Contract reminders falling due"
                      rows={reminders.data.reminders}
                      rowKey={(r) => r.id}
                      columns={[
                        {
                          key: 'contract',
                          header: 'Contract',
                          render: (r) => (
                            <Link to={`/contracts/${r.contract_id}`}>
                              <span className="mono smaller">{r.reference}</span>{' '}
                              <span className="truncate">{r.contract_title}</span>
                            </Link>
                          ),
                        },
                        {
                          key: 'type',
                          header: 'Deadline',
                          render: (r) => (
                            <Badge tone={r.reminder_type === 'notice_deadline' ? 'warn' : 'neutral'}>
                              {statusLabel(r.reminder_type)}
                            </Badge>
                          ),
                        },
                        { key: 'due', header: 'Due', render: (r) => date(r.due_date) },
                        {
                          key: 'value',
                          header: 'Annual value',
                          numeric: true,
                          render: (r) => <Money minor={r.annual_value_minor} blankIfZero />,
                        },
                      ]}
                    />
                  </Card>
                ) : null}
              </div>

              <p className="smaller muted">
                Every figure above is computed from posted transactions in {branding.organisationName}'s demonstration
                database at {dateTime(data.generatedAt)}. Nothing on this page is illustrative.
              </p>
            </div>
          );
        }}
      </AsyncPanel>
    </>
  );
}
